import Vue from 'vue';
import {ValidationObserver, ValidationProvider, extend, configure} from 'vee-validate';
import {required, email} from 'vee-validate/dist/rules';
import validateInn from '@/core/utils/validateInn';
import {validateSnils} from '@/core/utils/validateSnils';
import {datePlugin} from '@/core/plugins/dates';

Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);

configure({
    classes: {
        valid: 'is-valid',
        invalid: 'is-invalid',
        required: 'required',
    },
});

// Validation rules
extend('required', {
    ...required,
    message: 'Обязательное к заполнению поле',
});

extend('requiredImage', {
    ...required,
    message: 'Необходимо выбрать и сохранить изображение',
});

extend('requiredСheckbox', {
    ...required,
    message: 'Вам необходимо принять условия',
});

extend('requiredNumber', {
    validate: (value) => {
        return Boolean(+String(value).replace(/[^0-9.,]/g, '').replace(',', '.'));
    },
    message: 'Обязательное к заполнению поле',
});

extend('email', {
    ...email,
    message: 'Неверный формат email',
});

extend('min', {
    params: ['min', 'field'],
    validate: (value, {min}) => {
        return String(value).trim().length >= parseInt(min);
    },
    message: 'Поле {field} должно содержать не менее {min} символов',
});

extend('phone', {
    validate: (value) => {
        return /^(\+7\s\([0-9]{3}\)\s[0-9]{3}-[0-9]{2}-[0-9]{2})$/.test(value);
    },
    message: 'Неверный формат телефона',
});

extend('inn', {
    validate: (value) => {
        return /^\d{10}$|^\d{12}$/.test(value);
    },
    message: 'ИНН должен состоять из 10 или 12 цифр',
});

extend('legalInn', {
    validate: (value) => {
        return /^\d{10}$/.test(value);
    },
    message: 'Неверно указан ИНН',
});

extend('individualInn', {
    validate: (value) => {
        return /^\d{12}$/.test(value);
    },
    message: 'Неверно указан ИНН',
});

extend('correctInn', {
    validate: (value) => {
        const result = validateInn(value);

        return result;
    },
    message: 'Неверно указан ИНН',
});

extend('kpp', {
    validate: (value) => {
        return /^\d{9}$/.test(value);
    },
    message: 'Неверно указан КПП',
});

extend('bik', {
    validate: (value) => {
        return /^\d{7,9}$/.test(value);
    },
    message: 'Неверно указан БИК',
});

extend('onlyRuLetters', {
    validate: (value) => {
        return /^([А-Яа-яёЁ\-\s]+)$/g.test(value);
    },
    message: 'Допустимы только русские буквы',
});

extend('onlyRuLettersWithChars', {
    validate: (value) => {
        return /^([/-?!,.А-Яа-яёЁ0-9\-\s\W]+)$/g.test(value);
    },
    message: 'Допустимы только русские буквы',
});

extend('onlyLatinLetters', {
    validate: (value) => {
        return /^([a-zA-Z\-\s]+)$/g.test(value);
    },
    message: 'Допустимы только латинские буквы',
});

extend('selectEquality', {
    validate: (value, args) => {
        return value === args.join(',');
    },
    message: 'Выберите вариант из выпадающего списка',
});

extend('date', {
    validate: (value) => {
        // eslint-disable-next-line max-len
        const dateRegex = /^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/;

        return dateRegex.test(value);
    },
    message: 'Введите корректную дату',
});

extend('passportNumberAndSeries', {
    validate: (value) => {
        return /^([0-9]{4}\s[0-9]{6})$/.test(value);
    },
    message: 'Неверный номер паспорта',
});

extend('passportSeries', {
    validate: (value) => {
        return /^([0-9]{2}-[0-9]{2})$/.test(value);
    },
    message: 'Неверная серия паспорта',
});

extend('passportNumber', {
    validate: (value) => {
        return /^([0-9]{6})$/.test(value);
    },
    message: 'Неверный номер паспорта',
});

extend('passportDivisionCode', {
    validate: (value) => {
        return /^([0-9]{3}-[0-9]{3})$/.test(value);
    },
    message: 'Код подразделения должен состоять из 6 цифр',
});

extend('zipcode', {
    validate: (value) => {
        return /^\d{6}$/.test(value);
    },
    message: 'Индекс должен состоять из 6 цифр',
});

extend('birthdate', {
    params: ['years'],
    validate: (value, args) => {
        const maxBirthDate = datePlugin().subtract(+args?.years || 14, 'years').format('YYYY-MM-DD');
        const compareDate = datePlugin(value, 'DD.MM.YYYY').format('YYYY-MM-DD');

        return datePlugin(maxBirthDate).isSameOrAfter(compareDate);
    },
    message: 'Некорректная дата рождения',
});

extend('birthdateBetween', {
    params: ['minAge', 'maxAge'],
    validate: (value, {minAge, maxAge}) => {
        const date = datePlugin(value, 'DD.MM.YYYY');
        if (!date.isValid()) {
            return false;
        }
        const today = datePlugin();
        const minDate = today.subtract(maxAge, 'years');
        const maxDate = today.subtract(minAge, 'years');

        return date.isBetween(minDate, maxDate, null, '[]');
    },
    message: 'Возраст должен быть от {minAge} до {maxAge} лет',
});

extend('birthdate-minor', {
    validate: (value) => {
        const minBirthDate = datePlugin().subtract(17, 'years').format('YYYY-MM-DD');
        const compareDate = datePlugin(value, 'DD.MM.YYYY').format('YYYY-MM-DD');

        return datePlugin(minBirthDate).isSameOrBefore(compareDate);
    },
    message: 'Некорректная дата рождения',
});

extend('maxCurrentDate', {
    validate: (value) => {
        const maxCurrentDate = datePlugin().format('YYYY-MM-DD');
        const compareDate = datePlugin(value, 'DD.MM.YYYY').format('YYYY-MM-DD');

        return datePlugin(maxCurrentDate).isSameOrAfter(compareDate);
    },
    message: 'Некорректная дата. Дата не может быть больше текущей',
});

extend('minCurrentDate1900', {
    validate: (value) => {
        const minCurrentDate1900 = datePlugin('1900-01-01', 'YYYY-MM-DD').format('YYYY-MM-DD');
        const compareDate = datePlugin(value, 'DD.MM.YYYY').format('YYYY-MM-DD');

        return datePlugin(minCurrentDate1900).isSameOrBefore(compareDate);
    },
    message: 'Некорректная дата',
});

extend('cardNumber', {
    validate: (value) => {
        return /^([0-9]{4}\sXXXX\sXXXX\s[0-9]{4})$/.test(value);
    },
    message: 'Неверный формат номера карты',
});

extend('cadastralNumber', {
    validate: (value) => {
        return /^([0-9]{2}:[0-9]{2}:[0-9]{7}:[0-9]{1,5})$/.test(value);
    },
    message: 'Неверно указан кадастровый номер',
});

extend('bankAccountNumber', {
    validate: (value) => {
        return /^([0-9]{20})$/.test(value);
    },
    message: 'Неверно указан номер счета',
});

extend('lessthan', {
    params: ['comparable', 'target'],
    validate(value, {comparable, target}) {
        return parseFloat(Number(value) + Number(comparable)) <= Number(target);
    },
    message: 'Общее значение полей не должно превышать {target}',
});

extend('birthCertificateSeries', {
    validate: (value) => {
        return /^([А-ЯЁA-Z0-9-]+)$/g.test(value);
    },
    message: 'Неверно указана серия',
});

extend('namedBetween', {
    params: ['min', 'max', 'name'],
    validate(value, {min, max}) {
        return value.length >= min && value.length <= max;
    },
    message: 'Длина {name} должно быть между {min} и {max}',
});

extend('amountMoreThan', {
    params: ['sum'],
    validate(value, {sum}) {
        return parseFloat(value.replace(',', '.')) > parseFloat(sum);
    },
    message: 'Сумма должна быть больше {sum}',
});

extend('snils', {
    validate: (value) => {
        return validateSnils(String(value));
    },
    message: 'Неверно указан СНИЛС',
});

extend('vehicleRegNum', {
    validate: (value) => {
        return /^(^[А-ЯЁа-яё]{1}\d{3}[А-ЯЁа-яё]{2}\s\d{2,3})$/g.test(value);
    },
    message: 'Неверно указан номер автомобиля',
});

extend('contractMinDate', {
    validate: (value) => {
        const minDate = datePlugin('01.01.2020', 'DD.MM.YYYY').format('YYYY-MM-DD');
        const compareDate = datePlugin(value, 'DD.MM.YYYY').format('YYYY-MM-DD');

        return datePlugin(minDate).isSameOrBefore(compareDate);
    },
    message: 'Оформление вычета возможно, только при заключении договора кредитования не ранее 2020 года',
});

extend('minTaxPeriodYear', {
    validate: (value) => {
        const minYear = datePlugin('2020', 'YYYY');
        const compareYear = datePlugin(value, 'YYYY');

        return datePlugin(minYear).isSameOrBefore(compareYear);
    },
    message: 'Налоговые период должен быть не ранее 2020 года',
});

extend('yearTaxablePeriod', {
    validate: (value) => {
        const currentYear = new Date().getFullYear().toString();
        const minYear = datePlugin('2020', 'YYYY');
        const maxYear = datePlugin(currentYear, 'YYYY');
        const isYearValid = datePlugin(value, 'YYYY').isBetween(minYear, maxYear, 'year', '[]');

        return /^([0-9]{4})$/.test(value) && isYearValid;
    },
    message: 'Неверно указан год налогового периода',
});
